import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/layouts/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, User, ArrowLeft, Share2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { urlFor } from "@/lib/sanity";
import { fetchBlogBySlug } from "@/lib/api/blog";
import { BlogPost } from "@/types/blog";
import PortableTextRenderer from "@/components/features/blogs/PortableTextRender";
import { calculateReadTime } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

const BlogDetail = () => {
  const { blogId } = useParams();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const { language } = useLanguage();

  useEffect(() => {
    if (!blogId) return;

    fetchBlogBySlug(blogId, language)
      .then((data) => {
        if (!data) throw new Error("Blog not found");
        setBlog({
          ...data,
          readTime: calculateReadTime(data.content),
        });
      })
      .catch((err) => {
        setError(err.message);
        toast({
          title: language === "ar" ? "حدث خطأ" : "Error",
          description: language === "ar"
            ? "فشل في تحميل التدوينة. يرجى المحاولة مرة أخرى."
            : "Failed to load blog post. Please try again.",
          variant: "destructive",
        });
      })
      .finally(() => setLoading(false));
  }, [blogId, language, toast]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blog?.title,
        text: blog?.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: language === "ar" ? "تم نسخ الرابط!" : "Link copied!",
        description: language === "ar"
          ? "تم نسخ رابط التدوينة إلى الحافظة."
          : "Blog link has been copied to clipboard.",
      });
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pt-24 pb-16">
          <div className="container max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
              <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !blog) {
    return (
      <Layout>
        <div className="pt-24 pb-16">
          <div className="container max-w-4xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-gray-600 dark:text-gray-300 mb-4">
              {language === "ar"
                ? "لم يتم العثور على التدوينة"
                : "Blog Post Not Found"}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mb-8">
              {language === "ar"
                ? "التدوينة التي تبحث عنها غير موجودة أو تم حذفها."
                : "The blog post you're looking for doesn't exist or has been removed."}
            </p>
            <Button asChild>
              <Link to="/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {language === "ar" ? "العودة إلى المدونة" : "Back to Blog"}
              </Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <article className="pt-24 pb-16">
        <div className="container max-w-4xl mx-auto">
          <div className="mb-8">
            <Button variant="ghost" asChild>
              <Link to="/blog">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {language === "ar" ?"العودة الي المدونة" : "Back to Blogs"}
              </Link>
            </Button>
          </div>

          <header className="mb-8">
            <div className="mb-4 flex flex-wrap gap-2">
              {blog.categories.map((cat) => (
                <Badge key={cat.id} className="bg-nexus-teal text-white text-xs">
                  {cat.title}
                </Badge>
              ))}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-nexus-gray dark:text-white mb-6">
              {blog.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-600 dark:text-gray-300 mb-6">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5" />
                <span>{language === "ar" ? "الكاتب" : "Author"}: {blog.author.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>
                  {language === "ar" ? "تاريخ النشر" : "Published"}:{" "}
                  {new Date(blog.publishedAt).toLocaleDateString()}
                </span>
              </div>
              <span>
                {language === "ar" ? "مدة القراءة" : "Read Time"}: {blog.readTime}
              </span>
              <Button variant="ghost" size="sm" onClick={handleShare}>
                <Share2 className="h-4 w-4 mr-1" />
                {language === "ar" ? "مشاركة" : "Share"}
              </Button>
            </div>

            <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden mb-8">
              <img
                src={urlFor(blog.mainImage)}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </div>
          </header>

          <div className="prose prose-lg dark:prose-invert max-w-none mb-8">
            <PortableTextRenderer value={blog.body} />
          </div>

          {Array.isArray(blog.tags) && blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {blog.tags.map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          <Card className="bg-nexus-teal/5 border-nexus-teal/20">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-nexus-gray dark:text-white mb-4">
                {language === "ar"
                  ? "ابق على اطلاع مع كواليتي نيكسوس"
                  : "Stay Updated with Quality Nexus"}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {language === "ar"
                  ? "احصل على أحدث الأفكار حول اختبار البرمجيات وضمان الجودة مباشرة إلى بريدك الإلكتروني."
                  : "Get the latest insights on software testing and quality assurance delivered to your inbox."}
              </p>
              <Button className="bg-nexus-brand hover:bg-nexus-brand/90">
                {language === "ar"
                  ? "اشترك في النشرة البريدية"
                  : "Subscribe to Newsletter"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </article>
    </Layout>
  );
};

export default BlogDetail;
